import { Observable, Subject, filter } from 'rxjs';
import {
  parseRendererMessage,
  type EditorToRendererMessage,
  type RendererMessage,
  type RendererMessageType,
  type RendererToEditorMessage,
} from './messages';
import { RendererView } from './rendererView';

/** The editor's only line to its renderer: typed messages each way (contracts/renderer-bridge.json). */
export interface RendererBridge {
  /** Posts a message to the renderer; dropped while no renderer is attached. */
  send(message: EditorToRendererMessage): void;
  /** Messages of one type from the renderer. */
  on<T extends RendererMessageType>(type: T): Observable<RendererMessage<T>>;
  /** Every message from the renderer. */
  readonly received$: Observable<RendererToEditorMessage>;
  /** Every message the editor posted, attached renderer or not. */
  readonly sent$: Observable<EditorToRendererMessage>;
  /** Whether the renderer listed this message in its rendererLoaded `accepts`: one newer than some renderers is sent only then. */
  accepts(type: EditorToRendererMessage['type']): boolean;
}

/** Which windows the bridge hears from, and the origin it posts to: the host's, given when it makes the bridge. */
export interface RendererOrigins {
  /** Whether a message comes from an origin the renderer may speak from. */
  allows(event: MessageEvent): boolean;
  /** The origin messages to the renderer are posted to. */
  target(): string;
}

/** The bridge to a renderer in an iframe, over window.postMessage. */
export class PostMessageRendererBridge implements RendererBridge {
  private iframe: HTMLIFrameElement | null = null;
  private listening = false;
  private readonly receivedSubject = new Subject<RendererToEditorMessage>();
  private readonly sentSubject = new Subject<EditorToRendererMessage>();
  private readonly view = new RendererView();
  // What the loaded renderer said it accepts besides the messages every renderer takes: nothing until its rendererLoaded
  // says, and nothing once its iframe goes
  private accepted = new Set<string>();

  readonly received$ = this.receivedSubject.asObservable();
  readonly sent$ = this.sentSubject.asObservable();

  constructor(private readonly origins: RendererOrigins) {}

  /**
   * The renderer's iframe; messages from any other window are ignored. Another iframe's renderer has not yet said what
   * it accepts, so nothing newer than some renderers goes to it until it does.
   */
  attach(iframe: HTMLIFrameElement): void {
    if (this.iframe !== iframe) this.accepted = new Set();
    this.iframe = iframe;
    if (this.listening) return;
    this.listening = true;
    window.addEventListener('message', this.onMessage);
  }

  /** Drops the iframe only when it is still the attached one, so a remount keeps its newer iframe; what its renderer accepted goes with it. */
  detach(iframe: HTMLIFrameElement): void {
    if (this.iframe !== iframe) return;
    this.iframe = null;
    this.accepted = new Set();
  }

  send(message: EditorToRendererMessage): void {
    const shown = this.view.outbound(message);
    if (!shown) return;
    this.sentSubject.next(shown);
    this.iframe?.contentWindow?.postMessage(shown, this.origins.target());
  }

  on<T extends RendererMessageType>(type: T): Observable<RendererMessage<T>> {
    return this.received$.pipe(filter((message): message is RendererMessage<T> => message.type === type));
  }

  accepts(type: EditorToRendererMessage['type']): boolean {
    return this.accepted.has(type);
  }

  private readonly onMessage = (event: MessageEvent) => {
    if (!this.origins.allows(event)) return;
    // Origin alone admits any same-origin window; only the renderer's iframe speaks on this bridge
    const renderer = this.iframe?.contentWindow;
    if (!renderer || event.source !== renderer) return;
    const message = parseRendererMessage(event.data);
    if (!message) return;
    if (message.type === 'rendererLoaded') this.accepted = new Set(message.accepts ?? []);
    this.view.inbound(message);
    this.receivedSubject.next(message);
  };
}
